import React from 'react';
import classnames from 'classnames';
import { User, AlertCircle, Repeat } from 'react-feather';

import scrollIntoView from 'utils/scroll_into_view';
import { Button, ButtonGroup, Icon } from 'ui';
import RenderInBody from 'components/shared/render_in_body';
import ModalContainer from 'components/shared/modal/modal_container';
import LearnerInteraction from 'components/Practice/Learner/LearnerInteraction';
import LearnerSelectableResponse from 'components/Practice/Learner/LearnerSelectableResponse';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import StartChat from 'components/Practice/Learner/StartChat';
import ConversationComplete from './ConversationComplete';

class LearnerConversation extends React.Component {
  constructor(props) {
    super(props);

    const { interactions } = this.props.conversation;
    const totalInteractions = interactions.length;
    const responseChoices =
      totalInteractions > 0 ? interactions[0].responses : [];

    const finalCorrectResponse = interactions[
      totalInteractions - 1
    ].responses.find(response => response.correct);
    const startInDisabledState =
      props.isLessonFinished &&
      !props.conversation.required &&
      !this.props.lessonConversationComplete;

    this.state = {
      prompt: this.props.prompt,
      customerName:
        this.props.conversation.customerName ||
        this.props.conversation.customer_name,
      interactions: this.orderedInteractions(interactions),
      currentInteraction: this.props.lessonConversationComplete
        ? totalInteractions
        : 0,
      selectedResponses: [],
      activeResponse: this.props.lessonConversationComplete
        ? finalCorrectResponse
        : null,
      showTyping: this.props.isPractice,
      showResponses: startInDisabledState,
      disableResponses: true,
      conversationComplete: this.props.lessonConversationComplete,
      showCompleteModal: false,
      responseChoices,
      totalInteractions,
      isChatStarted:
        this.props.isPractice ||
        this.props.lessonConversationComplete ||
        startInDisabledState,
    };
  }

  // save the typing timeout so we can clear it if the hidden skip is clicked
  delayTimeoutId = null;

  componentDidMount() {
    if (this.props.isPractice) {
      this.startConversation();
    }
  }

  componentDidUpdate() {
    if (this.props.saveError && this.state.showCompleteModal) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ showCompleteModal: false });
    }
  }

  /**
   * Starts the conversation by typing the first customer question
   */
  startConversation() {
    if (this.state.totalInteractions === 0) return;
    const firstInteractionText = this.state.interactions[0].subject;
    this.delayTimeoutId = setTimeout(
      this.completeTyping,
      this.typingMilisecondsFromText(firstInteractionText),
    );
  }

  /**
   * Make sure the interactions are ordered by position. This should be ordered
   * in the JSON from the server but just in case there are issues we want to
   * be certian the conversation is in order or it will not make sense. We have
   * also had issues in the past with acts_as_list skipping numbers in positions
   *
   * @param {Array} interactions
   * @return {Array}
   */
  orderedInteractions = interactions =>
    interactions.sort((current, next) =>
      current.position < next.position ? -1 : 1,
    );

  /**
   * We want to shuffle the responses each time they are displayed
   * https://medium.com/@oldwestaction/randomness-is-hard-e085decbcbb2
   *
   * @param {Array} responses
   * @return {Array}
   */
  shuffleResponses = responses => {
    for (let i = responses.length - 1; i > 0; i -= 1) {
      const swapIndex = Math.floor(Math.random() * (i + 1));
      const currentCard = responses[i];
      const responseToSwap = responses[swapIndex];
      responses[i] = responseToSwap;
      responses[swapIndex] = currentCard;
    }
    return responses;
  };

  /**
   * We want to simulate how long the customer appears to be typing
   * based on the length of the text. We should also cap how long
   * the user has to wait in case of large blocks of text
   *
   * @param {String} text
   * @return {Integer}
   */
  typingMilisecondsFromText = text => {
    const wordsPerMinute = 450; // how fast the simulated customer types
    const maxDelay = 5000; // return faster if the text is huge
    const minDelay = 3000; // we want to add a minimum amount of time that, if the test is very short

    if (text == null) {
      return minDelay;
    }

    const wordCount = text.split(' ').length;
    const millisecondsInMinute = 60000;
    const fullDelay = Math.floor(
      millisecondsInMinute * (wordCount / wordsPerMinute),
    );

    if (fullDelay > maxDelay) {
      return maxDelay;
    }

    if (fullDelay < minDelay) {
      return minDelay;
    }

    return fullDelay;
  };

  /* used to build selected response */

  buildSelectedResponse = (
    activeResponse,
    currentInteraction,
    interactions,
  ) => {
    return {
      question_interaction_response_id: activeResponse.id,
      question_interaction_response_subject: activeResponse.subject,
      question_interaction_response_explanation: activeResponse.explanation,
      question_interaction_response_correct: activeResponse.correct,
      question_interaction_id: interactions[currentInteraction].id,
      question_interaction_subject: interactions[currentInteraction].subject,
      created_at: new Date().toJSON(),
    };
  };

  /**
   * If the selection is incorrect we show the selected response with the incorrect
   * indicator, explanation, and prevent the conversation from progressing.
   * When the seleciton is correct we show the correct indicator, explanation,
   * and delay the next customer interaction to simulate a customer typing.
   * If there are no more interactions we complete the conversation by showing
   * buttons at the bottom.
   *
   * @param {Object} activeResponse
   */
  selectResponse = activeResponse => {
    const { currentInteraction, interactions } = this.state;
    const moveToNextInteraction =
      this.hasNextInteraction && activeResponse.correct;
    const nextInteraction = moveToNextInteraction
      ? currentInteraction + 1
      : currentInteraction;
    const selectedResponses = this.state.selectedResponses.concat(
      this.buildSelectedResponse(
        activeResponse,
        currentInteraction,
        interactions,
      ),
    );
    if (activeResponse.correct && this.hasNextInteraction) {
      this.setState(
        {
          selectedResponses,
          activeResponse,
          currentInteraction: currentInteraction + 1,
          showTyping: true,
          disableResponses: true,
        },

        () => {
          const nextInteractionText =
            this.state.interactions[nextInteraction].subject;
          const nextInteractionDelay =
            this.typingMilisecondsFromText(nextInteractionText);
          this.scrollToActiveResponse(activeResponse);
          this.delayTimeoutId = setTimeout(
            this.completeTyping,
            nextInteractionDelay,
          );
        },
      );
    } else if (activeResponse.correct) {
      this.setState({
        selectedResponses,
        activeResponse,
        showResponses: false,
        conversationComplete: true,
      });
      if (!this.props.isPractice) {
        this.props.saveAttempt(selectedResponses);
      }
    } else {
      this.setState(
        {
          selectedResponses,
          activeResponse,
          disableResponses: true,
        },
        () => {
          this.scrollToActiveResponse(activeResponse);
          setTimeout(() => {
            this.setState({
              disableResponses: false,
              responseChoices: this.shuffleResponses(
                interactions[currentInteraction].responses,
              ),
            });
          }, 500);
        },
      );
    }
  };

  /**
   * Scroll the screen to the active response if it's out of view
   * so the user can see there is a correct/incorrect explanition
   */
  scrollToActiveResponse(activeResponse) {
    const activeSelector = `.OnDemandPracticeConversation-chatBubble.active-${activeResponse.id}`;
    const activeOffsetTop = $(activeSelector).offset().top;
    const documentScroll =
      document.scrollingElement || document.documentElement;

    if (activeOffsetTop <= documentScroll.scrollTop) {
      scrollIntoView(activeSelector);
    }
  }

  /**
   * A way to skip the delay set on the typing test before the next
   * interaction is shown
   */
  overwriteTyping = () => {
    if (this.state.totalInteractions === 0) return;
    clearTimeout(this.delayTimeoutId);
    this.completeTyping();
  };

  /**
   * Used for both the typing timer and the overwrite click
   */
  completeTyping = () => {
    const { interactions, currentInteraction } = this.state;
    this.setState({
      isChatStarted: true,
      showTyping: false,
      showResponses: true,
      disableResponses: false,
      responseChoices: this.shuffleResponses(
        interactions[currentInteraction].responses,
      ),
    });
  };

  /**
   * Checks if we are at the end of the conversation
   *
   * @return {boolean}
   */
  get hasNextInteraction() {
    const { currentInteraction, totalInteractions } = this.state;
    return currentInteraction + 1 < totalInteractions;
  }

  /**
   * Restart the conversation after completing
   */
  reloadConversation = () => {
    this.setState(
      {
        currentInteraction: 0,
        activeResponse: null,
        showTyping: true,
        showResponses: false,
        selectedResponses: [],
        disableResponses: true,
        responseChoices: this.props.conversation.interactions[0].responses,
        conversationComplete: false,
      },
      () => {
        if (!this.props.isPractice) {
          this.props.tryAgain();
        }
        this.startConversation();
      },
    );
  };

  render() {
    const {
      prompt,
      customerName,
      interactions,
      currentInteraction,
      showTyping,
      showResponses,
      disableResponses,
      responseChoices,
      conversationComplete,
      showCompleteModal,
      totalInteractions,
      activeResponse,
      isChatStarted,
    } = this.state;
    const shouldShowButtons =
      conversationComplete && !this.props.isPreviewing && this.props.isPractice;

    const responseStyleNames = classnames(
      'OnDemandPracticeConversation-responses Animated--fadeIn',
      {
        placeholder: !showResponses,
        disabled: disableResponses,
      },
    );
    const canRetryConversationQuestion =
      conversationComplete &&
      !this.props.isPractice &&
      !this.props.isLessonFinished;

    return (
      <div ref={this.elementRef}>
        <div className="d-f fd-c">
          <Text variant={TEXT_VARIANTS.TITLE_BOLD}>{this.props.title}</Text>
          <Text className="mb-32 mt-16" variant={TEXT_VARIANTS.SUBHEADER}>
            {prompt}
          </Text>
        </div>
        <div className="OnDemandPracticeConversation mb-128">
          <>
            <div className="OnDemandPracticeConversation-chatContainer">
              <div className="OnDemandPracticeConversation-header d-f fd-r ai-c">
                <User size={24} className="c-inherit mr-8" />
                <Text
                  className="OnDemandPracticeConversation-customer-name"
                  variant={TEXT_VARIANTS.SUBHEADER_BOLD}
                >
                  {customerName}
                </Text>
              </div>

              {!isChatStarted && (
                <StartChat onStartChat={this.completeTyping} />
              )}

              {isChatStarted &&
                interactions
                  .slice(0, currentInteraction + 1)
                  .map((interaction, position) => (
                    <LearnerInteraction
                      key={interaction.id}
                      interaction={interaction}
                      position={position}
                      currentInteraction={currentInteraction}
                      showTyping={showTyping}
                      totalInteractions={totalInteractions}
                      activeResponse={activeResponse}
                      conversationComplete={conversationComplete}
                    />
                  ))}
              {conversationComplete && !this.props.isPractice && (
                <ConversationComplete customerName={customerName} />
              )}

              {showTyping && isChatStarted && (
                <Text
                  onClick={this.overwriteTyping}
                  onKeyDown={this.overwriteTyping}
                  variant={TEXT_VARIANTS.BODY}
                  className="OnDemandPracticeConversation-customerTyping mt-24 mb-24 fs-i"
                  component="div"
                >
                  {customerName}{' '}
                  {t('practice.on_demand_practice.conversation.typing')}...
                </Text>
              )}
            </div>

            {!conversationComplete && !showTyping && isChatStarted && (
              <div className={responseStyleNames}>
                <Text
                  className="ta-r"
                  variant={TEXT_VARIANTS.SUBHEADER_BOLD}
                  component="h3"
                >
                  {t('practice.on_demand_practice.conversation.reply_title')}
                </Text>
                {responseChoices.map(response => (
                  <LearnerSelectableResponse
                    key={response.id}
                    response={response}
                    selectResponse={this.selectResponse}
                  />
                ))}
              </div>
            )}
            {canRetryConversationQuestion && (
              <div className="d-f jc-e mt-30">
                <Button
                  type="button"
                  onClick={this.reloadConversation}
                  loading={false}
                >
                  {t('practice.repeatability.practice_again_button')}
                </Button>
              </div>
            )}
          </>
          {shouldShowButtons && (
            <div className="OnDemandPracticeConversation-buttons">
              <Button
                disabled={this.props.saving}
                onClick={this.reloadConversation}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    this.reloadConversation();
                  }
                }}
                tabIndex="0"
                type="button"
                className="Button"
              >
                {t('common.try_again')}
              </Button>
              <Button
                disabled={this.props.saving}
                onClick={() => {
                  this.setState({ showCompleteModal: true });
                  this.props.saveAttempt(this.state.selectedResponses);
                }}
                tabIndex="0"
                type="button"
                className="Button ml-8"
              >
                {t('common.save')}
              </Button>
            </div>
          )}
          {!this.props.isPractice && this.props.saveError && (
            <div className="d-f ai-c pt-16 jc-e">
              <Text
                className="d-f ai-c c-error"
                variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}
              >
                <AlertCircle size="1.25rem" className="mr-4" />
                {t('practice.on_demand_practice.conversation.saving_error')}
                &nbsp;
              </Text>
              <button
                type="button"
                className="u-linkBtn"
                onClick={() =>
                  this.props.saveAttempt(this.state.selectedResponses)
                }
              >
                <Text className="d-f ai-c" variant={TEXT_VARIANTS.BODY_LINK}>
                  {t('practice.on_demand_practice.conversation.try_save_again')}
                  <Repeat size="1em" className="ml-4" />
                </Text>
              </button>
            </div>
          )}
        </div>

        {showCompleteModal && (
          <RenderInBody>
            <ModalContainer className="ModalTakeover-background Modal-background">
              <div className="ModalTakeover-container OnDemandPracticeConversation-completeContainer">
                <div className="Modal-header OnDemandPracticeConversation-completeHeader">
                  <div className="Modal-title OnDemandPracticeConversation-completeTitle">
                    {t(
                      'practice.on_demand_practice.conversation.complete_title',
                    )}
                  </div>
                </div>
                <Icon name="thumbsup_sparkle" />
                <h2 className="OnDemandPracticeConversation-completeSubtitle">
                  {t(
                    'practice.on_demand_practice.conversation.complete_subtitle',
                  )}
                </h2>
                <p>
                  {t('practice.on_demand_practice.conversation.complete_body')}
                </p>
                <ButtonGroup alignEnd>
                  <Button
                    disabled={this.props.saving}
                    className="CustomColor--backgroundColor"
                    onClick={this.props.showAttempt}
                  >
                    {t(
                      'practice.on_demand_practice.conversation.complete_view_results',
                    )}
                  </Button>
                </ButtonGroup>
              </div>
            </ModalContainer>
          </RenderInBody>
        )}
      </div>
    );
  }
}

export default LearnerConversation;
